import React, {useState, useEffect, useRef, useCallback, useMemo} from 'react'

import {
  Wrapper,
  LabelRow,
  LabelEl,
  SelectWrapper,
  SearchInput,
  IconSlot,
  HelperText,
  LabelIcon,
  LabelReq,
  OptionsPanel,
  OptionItem,
  NoOptionsText,
} from './Select.styles'

// ─────────────────────────────────────────────
// React 17-compatible unique id
// ─────────────────────────────────────────────

let idCounter = 0

function useUniqueId(prefix = 'select') {
  const idRef = useRef(null)

  if (idRef.current === null) {
    idRef.current = `${prefix}-${++idCounter}`
  }

  return idRef.current
}

// ─────────────────────────────────────────────
// Default Chevron Icon
// ─────────────────────────────────────────────

function ChevronDownIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
      <path
        d="M5 7.5L10 12.5L15 7.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// ─────────────────────────────────────────────
// Component
//
// Renders as a searchable combobox (text input + filtered listbox) rather
// than a native <select> — the option list only appears once the user
// starts typing, narrowing to matches, instead of dumping every option on
// open. External API (props in, onChange(event)/onBlur(event) out) is kept
// identical to the native-select version this replaced, so callers like
// SelectField.js don't need to change.
// ─────────────────────────────────────────────

export function Select({
  label,

  labelLeftIcon,

  helperText,

  errorText,

  leftIcon,

  rightIcon,

  options = [],

  placeholder = 'Select option',

  size = 'sm',

  variant = 'default',

  disabled = false,

  required = false,

  id: externalId,

  value,

  onChange,

  onBlur,

  ...nativeProps
}) {
  const autoId = useUniqueId('select')

  const selectId = externalId ?? autoId

  const helperId = `${selectId}-helper`

  const errorId = `${selectId}-error`

  const hasError = Boolean(errorText)

  const hasLeftIcon = Boolean(leftIcon)

  const hasRightIcon = Boolean(rightIcon)

  const hasLabelLeftIcon = Boolean(labelLeftIcon)

  const normalizedOptions = useMemo(
    () =>
      options.map(option =>
        typeof option === 'string' ? {label: option, value: option} : option,
      ),
    [options],
  )

  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  const wrapperRef = useRef(null)

  const selected =
    normalizedOptions.find(option => option.value === value) ?? null

  // Keep the input text showing the selected option's label whenever
  // it isn't actively being searched.
  useEffect(() => {
    if (!isOpen) setQuery(selected?.label ?? '')
  }, [selected, isOpen])

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return normalizedOptions.length <= 20 ? normalizedOptions : []
    return normalizedOptions.filter(option =>
      option.label.toLowerCase().includes(q),
    )
  }, [normalizedOptions, query])

  const commit = useCallback(
    optionValue => {
      onChange?.({target: {value: optionValue}})
      setIsOpen(false)
      setHighlightedIndex(-1)
    },
    [onChange],
  )

  // Clear the search text on open so typing starts from a blank slate
  // instead of the field being pre-filled with the current selection.
  const openList = useCallback(() => {
    if (disabled) return
    setIsOpen(true)
    setQuery('')
    setHighlightedIndex(-1)
  }, [disabled])

  // Close on outside click
  useEffect(() => {
    const handleOutside = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleOutside)

    return () => {
      document.removeEventListener('mousedown', handleOutside)
    }
  }, [])

  const handleKeyDown = e => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        openList()
      }
      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex(prev =>
        Math.min(prev + 1, filteredOptions.length - 1),
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex(prev => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const option = filteredOptions[highlightedIndex]
      if (option) commit(option.value)
    } else if (e.key === 'Escape') {
      setIsOpen(false)
      setQuery(selected?.label ?? '')
    }
  }

  return (
    <Wrapper ref={wrapperRef}>
      {label && (
        <LabelRow>
          {hasLabelLeftIcon && <LabelIcon>{labelLeftIcon}</LabelIcon>}

          <LabelEl htmlFor={selectId} variant={variant}>
            {label}
          </LabelEl>

          {required && (
            <LabelReq htmlFor={selectId} variant={variant}>
              *
            </LabelReq>
          )}
        </LabelRow>
      )}

      <SelectWrapper>
        {hasLeftIcon && (
          <IconSlot side="left" aria-hidden="true">
            {leftIcon}
          </IconSlot>
        )}

        <SearchInput
          id={selectId}
          type="text"
          role="combobox"
          size={size}
          variant={variant}
          disabled={disabled}
          hasError={hasError}
          hasLeftIcon={hasLeftIcon}
          hasRightIcon={hasRightIcon}
          aria-invalid={hasError}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-describedby={
            [errorText && errorId, helperText && helperId]
              .filter(Boolean)
              .join(' ') || undefined
          }
          value={query}
          placeholder={placeholder}
          onFocus={openList}
          onClick={openList}
          onChange={e => {
            setIsOpen(true)
            setQuery(e.target.value)
            setHighlightedIndex(-1)
          }}
          onKeyDown={handleKeyDown}
          onBlur={e => {
            setQuery(selected?.label ?? '')
            onBlur?.(e)
          }}
          {...nativeProps}
        />

        <IconSlot side="right" aria-hidden="true">
          {rightIcon || <ChevronDownIcon />}
        </IconSlot>

        {isOpen && (
          <OptionsPanel role="listbox">
            {filteredOptions.length === 0 && (
              <NoOptionsText>
                {query.trim() ? 'No matches found' : 'Start typing to search'}
              </NoOptionsText>
            )}

            {filteredOptions.map((option, index) => {
              const isSelected = option.value === value
              const isHighlighted = index === highlightedIndex

              return (
                <OptionItem
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  isSelected={isSelected}
                  isHighlighted={isHighlighted}
                  onMouseDown={e => e.preventDefault()}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => commit(option.value)}
                >
                  {option.label}
                </OptionItem>
              )
            })}
          </OptionsPanel>
        )}
      </SelectWrapper>

      {hasError && (
        <HelperText id={errorId} isError role="alert">
          {errorText}
        </HelperText>
      )}

      {!hasError && helperText && (
        <HelperText id={helperId}>{helperText}</HelperText>
      )}
    </Wrapper>
  )
}
